import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Loader2, Mail, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MadeBy } from "@/components/shared/MadeBy";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { OrganizerLinkRequest } from "@/components/arranger/OrganizerLinkRequest";
import { EASE } from "@/components/tickets/TicketUI";
import { errorMessage, orgAuthCode, orgAuthVerify } from "@/lib/api";
import { addSessions, getSessions, removeSession } from "@/lib/orgSession";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const Step = ({ k, children }) => {
  const reduce = useReducedMotion();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div key={k} initial={reduce ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }} transition={{ duration: 0.25, ease: EASE }}>{children}</motion.div>
    </AnimatePresence>
  );
};

/** Pick which organizer to open when one e-mail has access to several. */
export const SessionChooser = ({ sessions, onPick, title = "Kva lag vil du opne?" }) => (
  <div data-testid="organizer-chooser">
    <p className="t-mono text-muted-foreground">Arrangørside</p>
    <h1 className="t-h2 mt-3 text-balance">{title}</h1>
    <ul className="mt-8 divide-y divide-border border-y border-border">
      {sessions.map((s) => (
        <li key={s.token}>
          <button type="button" onClick={() => onPick(s)} className="group -mx-3 flex min-h-[64px] w-[calc(100%+24px)] items-center justify-between gap-4 rounded-[12px] px-3 py-3 text-left transition-colors hover:bg-card" data-testid={`organizer-chooser-${s.organizer.id}`}>
            <span className="min-w-0"><span className="block truncate text-[16px] font-medium">{s.organizer.name}</span><span className="t-mono-sm mt-0.5 block text-muted-foreground">{s.role_label || (s.role === "owner" ? "Ansvarleg" : "Medhjelpar")}{s.event_ids?.length ? ` · ${s.event_ids.length} arrangement` : ""}</span></span>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary transition-colors group-hover:bg-foreground group-hover:text-background"><ArrowRight className="h-4 w-4" /></span>
          </button>
        </li>
      ))}
    </ul>
  </div>
);

/**
 * Passwordless login for people on an organizer: e-mail → 6-digit code → session(s).
 * Also handles /arrangor/kode?e=…&k=… (one-tap link from the e-mail) and ?t=… (one-time link from the board).
 */
export const OrganizerLogin = ({ invalid = false }) => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [email, setEmail] = useState(params.get("e") || "");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState(() => (params.get("t") || (params.get("e") && params.get("k")) ? "auto" : getSessions().length ? "resume" : "email"));
  const [choices, setChoices] = useState(null);
  const [error, setError] = useState("");
  const auto = useRef(false);

  const finish = (data) => {
    const list = addSessions(data.sessions, data.person);
    const fresh = list.filter((s) => data.sessions.some((f) => f.token === s.token));
    if (fresh.length === 1) { toast.success(`Velkomen${data.person?.name ? `, ${data.person.name}` : ""}!`); navigate(`/arrangor/${fresh[0].token}`, { replace: true }); }
    else { setChoices(fresh); setStage("choose"); }
  };
  const send = useMutation({ mutationFn: () => orgAuthCode(email.trim()), onSuccess: () => { setStage("code"); setError(""); }, onError: (e) => setError(errorMessage(e, "Kunne ikkje sende koden – prøv igjen om litt")) });
  const verify = useMutation({
    mutationFn: (payload) => orgAuthVerify(payload),
    onSuccess: finish,
    onError: (e) => { setError(errorMessage(e, "Koden stemde ikkje")); setCode(""); if (stage === "auto") setStage(params.get("e") ? "code" : "email"); },
  });

  useEffect(() => {
    if (stage !== "auto" || auto.current) return;
    auto.current = true;
    const t = params.get("t");
    if (t) verify.mutate({ ticket: t });
    else verify.mutate({ email: params.get("e"), code: params.get("k") });
    setParams({}, { replace: true });
  }, [stage, params, setParams, verify]);

  const submitEmail = (e) => { e.preventDefault(); const v = email.trim(); if (!EMAIL_RE.test(v)) { setError("Skriv ei gyldig e-postadresse"); return; } setError(""); send.mutate(); };
  const stored = getSessions();

  return (
    <div className="mx-auto max-w-xl" data-testid="organizer-login" data-stage={stage}>
      {invalid && <div className="verdict-warn mb-8 rounded-[20px] border p-5" role="status" data-testid="organizer-invalid-banner"><p className="font-display text-[18px] font-semibold">Lenka er ugyldig eller utgått</p><p className="mt-1 text-[14px] text-foreground/80">Logg inn på nytt, så er du inne på eit blunk.</p></div>}
      <Step k={stage}>
        {stage === "auto" && (
          <div className="flex items-center gap-3 py-10 text-muted-foreground" data-testid="organizer-login-auto"><Loader2 className="h-5 w-5 animate-spin" /> Loggar deg inn …</div>
        )}

        {stage === "resume" && (
          <div>
            <SessionChooser sessions={stored} onPick={(s) => navigate(`/arrangor/${s.token}`)} title={stored.length === 1 ? "Velkomen tilbake" : "Velkomen tilbake – kva lag?"} />
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-[14px] text-muted-foreground">
              <button type="button" className="u-link min-h-[44px] font-medium text-foreground" onClick={() => setStage("email")} data-testid="organizer-login-other">Logg inn med ein annan e-post</button>
            </div>
          </div>
        )}

        {stage === "email" && (
          <div>
            <p className="t-mono text-muted-foreground">Arrangørside</p>
            <h1 className="t-h2 mt-3 text-balance">Logg inn utan passord</h1>
            <p className="mt-3 text-[16px] leading-relaxed text-muted-foreground">Skriv e-posten din, så sender vi ein kode. Ingen passord å hugse.</p>
            <form onSubmit={submitEmail} noValidate className="mt-8" data-testid="organizer-login-form">
              <label className="sr-only" htmlFor="org-login-email">E-post</label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input id="org-login-email" type="email" inputMode="email" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="namn@laget.no" className="h-12 rounded-[10px] bg-card text-[16px]" aria-invalid={!!error} data-testid="organizer-login-email" />
                <Button type="submit" size="lg" className="h-12 shrink-0 justify-between rounded-[10px] sm:px-6" disabled={send.isPending} data-testid="organizer-login-submit">{send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send kode <ArrowRight className="h-4 w-4" /></>}</Button>
              </div>
              {error && <p className="mt-2 text-[13px] text-destructive" data-testid="organizer-login-error">{error}</p>}
            </form>
            <ul className="mt-10 grid gap-4 text-[14px] text-muted-foreground sm:grid-cols-3">
              {[[Mail, "Koden kjem på e-post og gjeld i 10 minutt."], [ShieldCheck, "Du er logga inn på denne eininga i 30 dagar."], [Users, "Ansvarleg kan gje dørvakter og andre eigen tilgang."]].map(([Icon, t]) => <li key={t} className="flex gap-3"><Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /><span>{t}</span></li>)}
            </ul>
            {stored.length > 0 && <button type="button" className="u-link mt-6 min-h-[44px] text-[14px] font-medium text-foreground" onClick={() => setStage("resume")} data-testid="organizer-login-back-resume">Tilbake til laga du alt er logga inn på</button>}
            <Collapsible className="mt-10 border-t border-border pt-6">
              <CollapsibleTrigger className="u-link min-h-[44px] text-[14px] text-muted-foreground" data-testid="organizer-login-legacy-toggle">Har laget berre den gamle fellesslenkja?</CollapsibleTrigger>
              <CollapsibleContent><p className="mt-2 text-[14px] text-muted-foreground">Vi sender ho på nytt til e-posten laget er registrert med. Tips: når du er inne, legg til deg sjølv og andre som personar – så slepp de å dele éi lenkje.</p><OrganizerLinkRequest className="mt-4" compact /></CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {stage === "code" && (
          <div>
            <p className="t-mono text-muted-foreground">Arrangørside</p>
            <h1 className="t-h2 mt-3 text-balance">Sjekk innboksen</h1>
            <p className="mt-3 text-[16px] leading-relaxed text-muted-foreground">Har <span className="font-medium text-foreground">{email.trim()}</span> tilgang til ei arrangørside, ligg det ein sekssifra kode der no. Skriv han her – eller trykk på lenkja i e-posten.</p>
            <div className="mt-8" data-testid="organizer-login-code">
              <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); setError(""); if (v.length === 6 && !verify.isPending) verify.mutate({ email: email.trim(), code: v }); }} autoFocus inputMode="numeric" pattern="^[0-9]*$" disabled={verify.isPending} containerClassName="gap-2 sm:gap-3">
                <InputOTPGroup className="gap-2 sm:gap-3">
                  {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-14 w-11 rounded-[10px] border border-input bg-card font-display text-[24px] font-semibold first:rounded-l-[10px] last:rounded-r-[10px] sm:h-16 sm:w-12" data-testid={`organizer-code-slot-${i}`} />)}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <div className="mt-3 min-h-[24px] text-[13px]" aria-live="polite">
              {verify.isPending && <span className="inline-flex items-center gap-2 text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sjekkar koden …</span>}
              {!verify.isPending && error && <span className="text-destructive" data-testid="organizer-login-error">{error}</span>}
            </div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[14px] text-muted-foreground">
              <button type="button" className="u-link min-h-[44px] font-medium text-foreground" disabled={send.isPending} onClick={() => { setCode(""); setError(""); send.mutate(); toast.success("Ny kode er på veg"); }} data-testid="organizer-login-resend">Send ny kode</button>
              <button type="button" className="u-link min-h-[44px]" onClick={() => { setStage("email"); setCode(""); setError(""); }} data-testid="organizer-login-change-email">Bruk ein annan e-post</button>
            </div>
          </div>
        )}

        {stage === "choose" && choices && <SessionChooser sessions={choices} onPick={(s) => navigate(`/arrangor/${s.token}`)} />}
      </Step>

      <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 border-t border-border pt-6 text-[14px] text-muted-foreground">
        <span>Ny her? <Link to="/arranger" className="u-link font-medium text-foreground" data-testid="organizer-gate-arranger">Slik arrangerer du noko</Link></span>
        <span>Treng du hjelp? <Link to="/kontakt" className="u-link font-medium text-foreground" data-testid="organizer-gate-contact">Kontakt styret</Link></span>
      </div>
      <MadeBy className="mt-6" />
    </div>
  );
};

/** Drop a session that the server no longer accepts, so the login page does not keep offering it. */
export const forgetSession = (token) => { if (token) removeSession(token); };
